import { useCallback } from "react";

import { getConversationType } from "@/utils/helpers";
import { USER_TAG } from "@/utils/constants";
import type { TConversation } from "@stores/chat-domain.types";

import { useChatStore } from "@stores/chat.store";
import { chatWebSocketService } from "../chat-websocket.service";
import { WS_ACTIONS, WS_FETCH_LIMIT } from "../utils/chat-websocket.constants";
import type { TPaginationKey } from "../chat-websocket.types";
import type { TUseChatConversationsReturn } from "./use-chat-conversations.types";

/** Retorna ações para buscar, resetar e selecionar conversas via WebSocket. */
export function useChatConversations(): TUseChatConversationsReturn {
  const fetchConversations = useCallback(
    (
      chatType: string,
      lastKey: TPaginationKey = undefined,
      tags: string[] | null = null,
    ) => {
      const store = useChatStore.getState();
      if (chatType === "to") {
        store.setConversationListLoading("to", chatType);
      } else {
        store.setConversationListLoading("from", chatType);
      }
      chatWebSocketService.incrementPendingRequestCount(chatType);
      chatWebSocketService.sendAuthorized({ chatType, lastKey, tags });
    },
    [],
  );

  const setSelectedTags = useCallback((tags: string[]) => {
    useChatStore.getState().setSelectedTags(tags);
  }, []);

  const resetPendingRequestCount = (chatType: string) => {
    chatWebSocketService.resetPendingRequestCount(chatType);
  };

  // Agenda o refresh de não-lidas ao abrir uma conversa com notificação; o getUnreadCount
  const resetConversations = (chatType: string) => {
    const store = useChatStore.getState();
    if (chatType === "to") {
      store.resetConversationList("to");
    } else {
      store.resetConversationList("from");
    }
  };

  const setConversationTabs = useCallback((index: number) => {
    const store = useChatStore.getState();

    if (store.tabs.conversation === index) return;

    store.resetMessageList();
    store.setSelectedTags([]);
    store.setConversationChatType(getConversationType(index));
    store.setConversationTab(index);
  }, []);

  const setConversationActive = useCallback(
    (conversation: TConversation | null | undefined, chatId: string) => {
      const store = useChatStore.getState();

      if (conversation) {
        const isBlocked = conversation.userTag === USER_TAG.Blocked;
        if (!isBlocked) {
          store.setMessageListLoading(conversation.chatId);
          chatWebSocketService.send({
            action: WS_ACTIONS.getMessages,
            chatId: conversation.chatId,
            limit: WS_FETCH_LIMIT,
          });
        }

        if (conversation.unreadMessages > 0) {
          const direction: "from" | "to" =
            store.conversation.chatType === "from" ? "from" : "to";
          store.updateUnreadMessage(direction, {
            chatId: conversation.chatId,
            unreadMessages: 0,
          });
        }
      } else {
        store.resetMessageList();
      }

      store.resetMessageStatus();
      store.resetNewMessage();

      if (conversation?.chatId === chatId) {
        store.resetMessageUnread();
      }

      store.setConversationActive(
        conversation ? { ...conversation, unreadMessages: 0 } : undefined,
      );
    },
    [],
  );

  return {
    fetchConversations,
    setSelectedTags,
    resetPendingRequestCount,
    resetConversations,
    setConversationTabs,
    setConversationActive,
  };
}
